import type MetaModule from "../map/module/MetaModule";
import type { ILayerView } from "../model/Layer";
import type FormatterFuncs from "./formatterFuncs";
import type Util from "./util";

const es = global.es; // TODO Remove dependency!

export default class LegendUtil {
    private util: Util;

    constructor(util: Util) {
        this.util = util;
    }

    /**
     * 
     * @param layer 
     * 
     * @returns 
     */
    private isMapScopeLayerView(layer: ILayerView): boolean {
        return this.util.map.isMapScopeLayerView(layer.$type);
    }

    /**
     * 
     * @param layerModule 
     * @param analysisId 
     * @param datasetModel 
     * 
     * @returns 
     */
    private isMapScope(layerModule, analysisId: number|undefined, datasetModel: ILayerView): JQueryPromise<boolean> {
        if (!analysisId) {
            return es.util.deferred.resolved(this.isMapScopeLayerView(datasetModel));
        }

        return es.api.analysis.get(analysisId).then((analysis) => {
            const generatedLayerViewIds = es.api.analysis.getGeneratedLayerViewIds(analysis);
            const sourceId = es.api.analysis.getSourceLayerViewIds(analysis).find((id, index) => {
                return generatedLayerViewIds[index] === datasetModel.id;
            });

            return layerModule.list().some((layerView) => {
                return layerView.getId() === sourceId && this.isMapScopeLayerView(layerView);
            });
        });
    }

    /**
     * 
     * @param model 
     * 
     * @returns 
     */
    private isColorPerFeatureStyleAvailable(model: ILayerView): boolean {
        return !!(model.styleSettings.colorPerFeatureSettings ||  // TODO FIXME This one should be enough... // bang bang you are a boolean
        (model.$type === "TerritoryLayerView" && model.geomType == 'POLYGON') || // Accounts-only don't have per feature style, only areas
        model.$type === "ManageTerritoryMainLayerView");
    }

    /**
     * This is to catch duplicates in analysis results. It assumes it is the same value
     * if it has the same label and color
     * 
     * @param feature 
     * @param index 
     * @param features 
     * 
     * @returns 
     */
    private isNotDuplicate(feature, index: number, features: any[]): boolean {
        for (let k = 0; k < index; k++) {
            if (feature.label === features[k].label && feature.attrs.ES_COLOR === features[k].attrs.ES_COLOR) {
                return false;
            }
        }

        return true;
    }

    /**
     * 
     * @param model 
     * 
     * @returns 
     */
    private getTerritoryFeatures(model: ILayerView): JQueryPromise<any[]> {
        if (!(model.styleSettings.colorPerFeatureSettings && model.styleSettings.colorPerFeatureSettings.enabled)) {
            return es.util.deferred.resolved();
        }

        return es.api.feature.render({
                "layers": [{
                    "id": model.id,
                    "criteria": model.criteria,
                    "sortOrder": {
                        "attr": model.styleSettings.labelSettings.labelAttrs[0]
                    }
                }],
                "format": {
                    "getAttrs": true,
                    "includeInternalAttrs": [model.id],
                    "srid": -1
                }
        }).then((rs) => {
            const features = rs.layers[0].features.filter(this.isNotDuplicate);
            var Intl = Intl ? Intl : undefined; // ipad seems to have problems with undefined variable
            if (Intl) {
                const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: "base"});
                features.sort((a, b) => collator.compare(a.label, b.label));
            }
            return features;
        });
    }

    /**
     * this should be on util/attribute.js but in there we don't have underscore library, welp!
     * 
     * @param attributeInfo array with attribute information
     * 
     * @returns array of attributes ordered by title
     */
    private getTitleOrderedAttributeList (attributeInfo): object {
        const orderedTitles = Object.values(attributeInfo).sort();
        const invertedAttributeInfo = this.util.invertValues(attributeInfo);
        const attributeNamesOrderedByTitle = {};
        for (let i = 0; i < orderedTitles.length; i++) {
            const title = orderedTitles[i];
            const name = invertedAttributeInfo[title];
            attributeNamesOrderedByTitle[name] = title;
        }

        return attributeNamesOrderedByTitle;
    }
    
    /**
     * 
     * @param mapMeta 
     * @param layerModule 
     * @param datasetModel 
     * @param formatterFuncs 
     * 
     * @returns 
     */
    getDatasetConfiguration(mapMeta: MetaModule, layerModule, datasetModel: ILayerView, formatterFuncs: FormatterFuncs): JQueryPromise<any> {
        const styleSettings = datasetModel.styleSettings; // just to speed up a bit

        const hasColorThematic = styleSettings.colorThematicSettings && styleSettings.colorThematicSettings.enabled;
        const hasSizeThematic = styleSettings.sizeThematicSettings && styleSettings.sizeThematicSettings.enabled;
        const hasSymbolThematic = styleSettings.symbolThematicSettings && styleSettings.symbolThematicSettings.enabled;

        const hasColorOverride = hasColorThematic && !!styleSettings.colorThematicSettings.overrideThematic;
        const hasSymbolOverride = hasSymbolThematic && !!styleSettings.symbolThematicSettings.overrideThematic;

        return $.when(
            hasColorThematic ? (hasColorOverride ? styleSettings.colorThematicSettings.overrideThematic : es.api.thematic.getColorThematic(datasetModel.id, styleSettings.colorThematicSettings.attributeName)) : null,
            hasSizeThematic ? es.api.thematic.getSizeThematic(datasetModel.id, styleSettings.sizeThematicSettings.attributeName) : null,
            hasSymbolThematic ? (hasSymbolOverride ? styleSettings.symbolThematicSettings.overrideThematic : es.api.thematic.getSymbolThematic(datasetModel.id, styleSettings.symbolThematicSettings.attributeName)) : null,
            this.getTerritoryFeatures(datasetModel),
            this.isMapScope(layerModule, mapMeta.getAnalysisId(), datasetModel)
        ).then((colorThematic, sizeThematic, symbolThematic, territoryFeatures, isMapScope) => {
            if (territoryFeatures) {
                // TOR9-9879 - Prepare label for rendering (i.e. remove ES_NEWLINE)
                territoryFeatures.forEach((f) => {
                    f.label = this.util.label.render(f.label);
                });
            }
            
            const hasColorPerFeature = this.isColorPerFeatureStyleAvailable(datasetModel);
            datasetModel.isMapScope = isMapScope;

            const attributesInfo = layerModule.getById(datasetModel.id).getLayerInfo().attributes;
            // all but dynamic attributes
            let allAttributeNames = datasetModel.attributeNames.filter((an) => {
                return !datasetModel.dynamicAttributes.get('name', an);
            });
            if (datasetModel.thematicableAttributeNames) {
                allAttributeNames = allAttributeNames.filter((a) => datasetModel.thematicableAttributeNames.includes(a));
            }
            
            let hotSpotAttributeTitle;
            if (styleSettings.heatMapSettings && styleSettings.heatMapSettings.attr) {
                hotSpotAttributeTitle = attributesInfo.find((a) => a.name == styleSettings.heatMapSettings.attr).title;
            }

            let allAttributes;
            let numericAttributes;

            const thematicableAttributeNames = layerModule.getById(datasetModel.id).getThematicableAttributeNames();
            if (thematicableAttributeNames) {
                allAttributes = this.util.attributes.getAttributesTitleMap(thematicableAttributeNames, attributesInfo);
                numericAttributes = this.util.attributes.getAttributesTitleMap(this.util.attributes.getNumericAttributes(thematicableAttributeNames, attributesInfo), attributesInfo);
            } else {
                allAttributes = this.getTitleOrderedAttributeList(this.util.attributes.getAttributesTitleMap(allAttributeNames, attributesInfo));
                var numericAttributeNames = this.util.attributes.getNumericAttributes(allAttributeNames, attributesInfo);
                numericAttributes = this.getTitleOrderedAttributeList(this.util.attributes.getAttributesTitleMap(numericAttributeNames, attributesInfo));
            }

            return {
                'dataset': datasetModel,
                'toggleAvailable': (allAttributeNames.length > 0 || !!styleSettings.labelSettings),
                'allAttributes': allAttributes,
                'numericAttributes': numericAttributes,
                'Color': {
                    'thematic': colorThematic,
                    'territoryFeatures': territoryFeatures,
                    'isDateType' : this.util.attributes.isDateType(colorThematic ? colorThematic.attribute : null, attributesInfo),
                    'attrType' : this.util.attributes.getAttrType(colorThematic ? colorThematic.attribute : null, attributesInfo)
                },
                'Size': {
                    'thematic': sizeThematic
                },
                'Symbol': {
                    'thematic': symbolThematic,
                    'isDateType' : this.util.attributes.isDateType(symbolThematic ? symbolThematic.attribute : null, attributesInfo),
                    'attrType' : this.util.attributes.getAttrType(symbolThematic ? symbolThematic.attribute : null, attributesInfo)
                },
                'HotSpot': {
                    'hotSpotFilter': styleSettings.heatMapSettings,
                    'attributeTitle': hotSpotAttributeTitle ? hotSpotAttributeTitle : ''
                },
                'isColorPerFeature': hasColorPerFeature,
                'territoryFeatures': territoryFeatures,
                'formatterFuncs': formatterFuncs
            };
        });
    };
}
